import { Injectable } from '@angular/core';
import { AccessControl } from '@pncl/common-models';
import { TabId } from '../settings/settings.constants';
import { CapabilityTierService } from './capability-tier.service';
import { FlagsService, FLAGS } from './flags.service';
import Capability = AccessControl.Capability;

/**
 * Centralized service for managing settings-related functionality.
 *
 * Current features:
 * - Tab visibility control via feature flags and tier capabilities
 *
 * Future extensibility:
 * - Form state management
 * - Settings validation helpers
 * - Settings navigation logic
 */
@Injectable({
  providedIn: 'root',
})
export class SettingsManagerService {
  /**
   * Tabs controlled via Optimizely SETTINGS_TABS_VISIBILITY variables.
   * Capability-gated tabs (see CAPABILITY_GATED_TABS) are not listed here.
   */
  private readonly OPTIMIZELY_GATED_TABS: readonly string[] = [TabId.LTI];

  /** Tab → capability required to show that tab. Unmapped tabs are not capability-gated. */
  private readonly CAPABILITY_GATED_TABS: Readonly<Partial<Record<TabId, Capability>>> = {
    [TabId.API]: Capability.API_AND_WEBHOOKS,
    [TabId.WEBHOOKS]: Capability.API_AND_WEBHOOKS,
    [TabId.LMS_AND_SIS]: Capability.LMS_AND_SIS,
  };

  /**
   * Tabs hidden by Optimizely SETTINGS_TABS_VISIBILITY (explicitly false).
   * Capability-gated tabs are evaluated at call time in isTabHidden, not cached here.
   */
  private readonly optimizelyHiddenTabs: Set<string>;

  constructor(
    private flagsService: FlagsService,
    private capabilityTierService: CapabilityTierService,
  ) {
    this.optimizelyHiddenTabs = this.computeOptimizelyHiddenTabs();
  }

  /**
   * Tabs whose SETTINGS_TABS_VISIBILITY flag variable is explicitly false.
   * Safe to cache at construct time — flags are loaded at app init.
   */
  private computeOptimizelyHiddenTabs(): Set<string> {
    const flagVars = this.flagsService.featureFlagsVariables[FLAGS.SETTINGS_TABS_VISIBILITY];
    const hidden = new Set<string>();

    this.OPTIMIZELY_GATED_TABS.forEach((tabKey) => {
      if (flagVars?.[tabKey] === false) {
        hidden.add(tabKey);
      }
    });

    return hidden;
  }

  /**
   * Checks if a specific tab should be hidden based on feature flags and capabilities.
   *
   * @param tabKey - The tab identifier (e.g., TabId.LTI, TabId.WEBHOOKS, TabId.API)
   * @returns true if the tab should be hidden, false otherwise
   *
   * Note: Feature flags are fetched at app initialization and are guaranteed
   * to be available before settings panel is opened. Capability is evaluated
   * at call time (settings open) so enabled_capabilities are already loaded.
   */
  public isTabHidden(tabKey: string): boolean {
    const requiredCapability = this.CAPABILITY_GATED_TABS[tabKey as TabId];
    if (
      requiredCapability &&
      !this.capabilityTierService.shouldEnableCapability(requiredCapability)
    ) {
      return true;
    }
    return this.optimizelyHiddenTabs.has(tabKey);
  }

  // ========================================
  // FUTURE: Add other settings-related functionality here
  // ========================================
  // Examples:
  // - Tab navigation state management
  // - Form dirty state tracking across panels
  // - Settings preferences caching
  // - etc.
}
